// formatters.ts
import { FormatConfig } from "./formatConfig";

/**
 * INTERNAL: Normalize UTC input and return LOCAL Date
 */
function utcToLocalDate(
  value: Date | string | number | undefined
): Date | null {
  if (!value) return null;

  if (value instanceof Date) {
    return new Date(value.getTime());
  }

  if (typeof value === "number") {
    // Epoch milliseconds (UTC)
    return new Date(value);
  }

  // ISO with timezone info (Z or ±hh:mm)
  if (/[zZ]|[+-]\d{2}:\d{2}$/.test(value)) {
    return new Date(value);
  }

  // Assume UTC string, force UTC
  return new Date(`${value}Z`);
}

/**
 * DATE FORMATTING (UTC → Local)
 */
export function formatDate(
  value: Date | string | undefined,
  config: FormatConfig
): string {
  if (!value) return "";

  const date = typeof value === "string" ? new Date(value) : value;
  if (isNaN(date.getTime())) return "";

  const dd = String(date.getDate()).padStart(2, "0");
  const mm = String(date.getMonth() + 1).padStart(2, "0");
  const yyyy = date.getFullYear();
  const yy = String(yyyy).slice(-2);
  const monthShort = new Intl.DateTimeFormat("en-US", { month: "short" }).format(date);

  const sep = config.dateSeparator;

  switch (config.dateFormat) {
    case "DDMMYY":
      return `${dd}${sep}${mm}${sep}${yy}`;
    case "DDMMYYYY":
      return `${dd}${sep}${mm}${sep}${yyyy}`;
    case "MMDDYY":
        return `${mm}${sep}${dd}${sep}${yy}`;
    case "MMDDYYYY":
        return `${mm}${sep}${dd}${sep}${yyyy}`;
    case "YYYYMMDD":
      return `${yyyy}${sep}${mm}${sep}${dd}`;
    case "DDMonthYYYY":
      return `${dd}${sep}${monthShort}${sep}${yyyy}`;
    case "YYMMDD":
      return `${yy}${sep}${mm}${sep}${dd}`;
    
    default:
      return `${mm}${sep}${dd}${sep}${yyyy}`;
  }
}

/**
 * DATETIME FORMATTING (UTC → Local)
 */
export function formatDateTime(
  value: Date | string | number | undefined,
  config: FormatConfig
): string {
  const date = utcToLocalDate(value);
  if (!date || isNaN(date.getTime())) return "";

  const dd = String(date.getDate()).padStart(2, "0");
  const mm = String(date.getMonth() + 1).padStart(2, "0");
  const yyyy = date.getFullYear();
  const yy = String(yyyy).slice(-2);
  const monthShort = new Intl.DateTimeFormat("en-US", { month: "short" }).format(date);


  const hh = String(date.getHours()).padStart(2, "0");
  const min = String(date.getMinutes()).padStart(2, "0");
  const ss = String(date.getSeconds()).padStart(2, "0");

  const sep = config.dateSeparator;
  const time = `${hh}:${min}:${ss}`;

  switch (config.dateFormat) {
    case "DDMMYY":
      return `${dd}${sep}${mm}${sep}${yy} ${time}`;
    case "DDMMYYYY":
      return `${dd}${sep}${mm}${sep}${yyyy} ${time}`;
    case "MMDDYY":
        return `${mm}${sep}${dd}${sep}${yy} ${time}`;
    case "MMDDYYYY":
        return `${mm}${sep}${dd}${sep}${yyyy} ${time}`;
    case "YYYYMMDD":
      return `${yyyy}${sep}${mm}${sep}${dd} ${time}`;
    case "DDMonthYYYY":
      return `${dd}${sep}${monthShort}${sep}${yyyy} ${time}`;
    case "YYMMDD":
      return `${yy}${sep}${mm}${sep}${dd} ${time}`;
    
    default:
      return `${mm}${sep}${dd}${sep}${yyyy} ${time}`;
  }
}

/**
 * NUMBER FORMATTING
 */
export function formatNumber(
  value: number | null | undefined,
  config: FormatConfig,
  decimals?: number
): string {
  if (value == null || isNaN(value)) return "";

  const valueStr = value.toString();
  const [intPart, rawDecPart = ""] = valueStr.split(".");

  const intWithThousands = intPart.replace(
    /\B(?=(\d{3})+(?!\d))/g,
    config.thousandSeparator
  );

  // If decimals is explicitly 0 → no decimals shown
  if (decimals === 0) {
    return intWithThousands;
  }

  // Determine decimal part
  let decPart: string;
  if (decimals == null) {
    // ✅ Preserve existing decimals
    decPart = rawDecPart;
  } else {
    // ✅ Truncate to requested decimals (NO rounding)
    decPart = rawDecPart.slice(0, decimals).padEnd(decimals, "0");
  }

  if (!decPart) return intWithThousands;

  return `${intWithThousands}${config.decimalSeparator}${decPart}`;
}


/**
 * TIME FORMATTING (UTC → Local)
 */
export function formatTime(
  value: Date | string | number | undefined,
  options?: {
    showSeconds?: boolean;
    use24Hour?: boolean;
  }
): string {
  const date = utcToLocalDate(value);
  if (!date || isNaN(date.getTime())) return "";

  const showSeconds = options?.showSeconds ?? false;
  const use24Hour = options?.use24Hour ?? true;

  const rawHours = date.getHours();
  const hours = use24Hour
    ? String(rawHours).padStart(2, "0")
    : String(((rawHours + 11) % 12) + 1);

  const minutes = String(date.getMinutes()).padStart(2, "0");
  const seconds = String(date.getSeconds()).padStart(2, "0");

  if (use24Hour) {
    return showSeconds
      ? `${hours}:${minutes}:${seconds}`
      : `${hours}:${minutes}`;
  }

  const ampm = rawHours >= 12 ? "PM" : "AM";
  return showSeconds
    ? `${hours}:${minutes}:${seconds} ${ampm}`
    : `${hours}:${minutes} ${ampm}`;
}